/**
 * Hark pairing from the web (plan §12.4).
 *
 * Two ways in:
 *   - /pair            the customer starts here; we show a code to type into Hark, and complete
 *                      the handshake from this tab once they confirm.
 *   - /pair/:code      the customer followed the pairing URL; the code is prefilled.
 *
 * The completion secret never leaves this tab except in the completion request, and the issued
 * token is shown exactly once (§12.4 step 6).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api } from "../api/client.js";
import { Banner, ErrorNote, Spinner, TokenModal, useCopy } from "../components/ui.js";
import { useSession } from "../state/session.js";
import type { PairingComplete, PairingStart } from "../api/types.js";

export default function Pair() {
  const { me } = useSession();
  const { code: codeParam } = useParams<{ code?: string }>();
  const [session, setSession] = useState<PairingStart | null>(null);
  const [result, setResult] = useState<PairingComplete | null>(null);
  const [deviceLabel, setDeviceLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [copied, copy] = useCopy();

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.pairingStart(deviceLabel.trim() || undefined);
      setSession(next);
      setSecondsLeft(next.expiresInSeconds);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [deviceLabel]);

  // Countdown so the customer knows the code is short-lived rather than broken.
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const complete = useCallback(async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api.pairingComplete({
          pairingCode: session.pairingCode,
          secret: session.pairingSecret,
          ...(deviceLabel.trim() ? { deviceLabel: deviceLabel.trim() } : {}),
        }),
      );
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [session, deviceLabel]);

  if (!me) return null;
  const ready = me.deployment?.status === "READY";
  const expired = secondsLeft !== null && secondsLeft <= 0;
  const notReady = error instanceof ApiError && (error.code === "conflict" || error.code === "not_found");

  return (
    <div className="wrap--narrow">
      <div className="page-head">
        <p className="eyebrow">Hark</p>
        <h1>Pair a device</h1>
        <p className="lede">
          Hark is our companion app for capturing things on the go. It is optional; your Construct
          works perfectly without it.
        </p>
      </div>

      {notReady ? (
        <Banner kind="warn" title="Your Construct is not ready yet">
          Pairing needs a running Construct. <Link to="/dashboard">Check its status</Link>
        </Banner>
      ) : error ? (
        <ErrorNote error={error} />
      ) : null}

      {!session ? (
        <div className="card">
          <h2>Start pairing</h2>
          <p className="muted">
            We will show you a short code. Type it into Hark, then come back here and confirm.
          </p>
          <label className="field">
            <span className="field__label">Name this device (optional)</span>
            <input
              className="input"
              type="text"
              maxLength={64}
              placeholder={codeParam ? "iPhone" : "My phone"}
              value={deviceLabel}
              onChange={(e) => setDeviceLabel(e.target.value)}
            />
          </label>
          <div className="btn-row">
            <button type="button" className="btn btn--primary" disabled={!ready || busy} onClick={() => void start()}>
              {busy ? <Spinner label="Starting" /> : null}
              {busy ? "Starting" : "Get a pairing code"}
            </button>
          </div>
        </div>
      ) : (
        <div className="card card--accent">
          <h2>Your pairing code</h2>
          <p className="muted">Open Hark, choose "Connect my Construct", and enter this code.</p>
          <div
            className="token"
            style={{ fontSize: "1.9rem", letterSpacing: ".2em", textAlign: "center", padding: 22 }}
          >
            {session.pairingCode}
          </div>
          <div className="btn-row" style={{ marginTop: 14 }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => copy(session.pairingCode)}>
              {copied ? "Copied" : "Copy code"}
            </button>
          </div>

          <p className="mono small muted" style={{ marginTop: 18 }}>
            {expired
              ? "This code has expired."
              : `Expires in ${Math.floor((secondsLeft ?? 0) / 60)}:${String((secondsLeft ?? 0) % 60).padStart(2, "0")}`}
          </p>

          <div className="btn-row">
            {expired ? (
              <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void start()}>
                Get a new code
              </button>
            ) : (
              <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void complete()}>
                {busy ? "Finishing" : "I entered it, finish pairing"}
              </button>
            )}
            <Link className="btn btn--ghost" to="/dashboard">
              Not now
            </Link>
          </div>
        </div>
      )}

      <div style={{ marginTop: 28 }}>
        <Banner kind="info" title="What Hark gets">
          A key that lets it capture into, and recall from, your Construct, nothing more. You can
          revoke it from the connections screen at any time, and doing so affects nothing else.
        </Banner>
      </div>

      {result ? (
        <TokenModal
          title="Hark is paired"
          token={result.credential.token}
          endpointUrl={result.endpoint.apiUrl}
          onClose={() => {
            setResult(null);
            setSession(null);
          }}
        />
      ) : null}
    </div>
  );
}
